/**
 * Statistics Manager for tracking key-degree specific performance
 * Fixed to use the same key system as MusicTheory module
 * Enhanced to support mode-specific tracking for forward and inverse challenges
 */

import { MusicTheory } from './music-theory.js';
import { ChallengeType } from './challenge-types.js';

interface Attempt {
  correct: boolean;
  time: number;
  timestamp: number;
  mode?: ChallengeType; // Added for mode-specific tracking
}

interface DegreeStats {
  attempts: number;
  correct: number;
  totalTime: number;
  bestTime: number | null;
  worstTime: number | null;
  recentAttempts: Attempt[];
}

interface KeyStats {
  [degree: number]: DegreeStats;
}

interface Stats {
  [key: string]: KeyStats;
}

interface ModeStats {
  forward: Stats;
  inverse: Stats;
}

interface OverallStats {
  totalAttempts: number;
  totalCorrect: number;
  accuracy: number;
  averageTime: number;
}

interface CombinationStats {
  key: string;
  degree: number;
  accuracy: number;
  averageTime: number;
  attempts: number;
  recentTrend: string;
}

export class StatisticsManager {
  private stats: Stats; // Legacy stats for backward compatibility
  private modeStats: ModeStats;

  constructor() {
    this.stats = this.loadStats();
    this.modeStats = this.loadModeStats();
  }

  /**
   * Initialize empty statistics structure
   * Uses the same keys as MusicTheory.KEYS to avoid mismatches
   */
  initializeEmptyStats(): Stats {
    const stats: Stats = {};

    // Use the same key system as MusicTheory to avoid mismatches
    for (const key of MusicTheory.KEYS) {
      stats[key] = {};
      for (let degree = 2; degree <= 7; degree++) {
        stats[key][degree] = {
          attempts: 0,
          correct: 0,
          totalTime: 0,
          bestTime: null,
          worstTime: null,
          recentAttempts: [] // Last 10 attempts for trend analysis
        };
      }
    }

    return stats;
  }

  /**
   * Load statistics from localStorage
   */
  loadStats() {
    try {
      const saved = localStorage.getItem('scale-degree-stats');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all keys and degrees exist (for version compatibility)
        const empty = this.initializeEmptyStats();
        return this.mergeStats(empty, parsed);
      }
    } catch (error) {
      console.warn('Failed to load stats from localStorage:', error);
    }
    
    return this.initializeEmptyStats();
  }

  /**
   * Load mode-specific statistics from localStorage
   */
  loadModeStats(): ModeStats {
    try {
      const saved = localStorage.getItem('scale-degree-mode-stats');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all keys and degrees exist for both modes
        const emptyForward = this.initializeEmptyStats();
        const emptyInverse = this.initializeEmptyStats();
        
        return {
          forward: parsed.forward ? this.mergeStats(emptyForward, parsed.forward) : emptyForward,
          inverse: parsed.inverse ? this.mergeStats(emptyInverse, parsed.inverse) : emptyInverse
        };
      }
    } catch (error) {
      console.warn('Failed to load mode stats from localStorage:', error);
    }
    
    return {
      forward: this.initializeEmptyStats(),
      inverse: this.initializeEmptyStats()
    };
  }

  /**
   * Merge existing stats with empty structure (for upgrades)
   */
  mergeStats(empty: Stats, existing: any): Stats {
    for (const key in empty) {
      if (existing[key]) {
        for (const degree in empty[key]) {
          if (existing[key][degree]) {
            empty[key][degree] = { ...empty[key][degree], ...existing[key][degree] };
          }
        }
      }
    }
    return empty;
  }

  /**
   * Save statistics to localStorage
   */
  saveStats() {
    try {
      localStorage.setItem('scale-degree-stats', JSON.stringify(this.stats));
    } catch (error) {
      console.error('Failed to save stats to localStorage:', error);
    }
  }

  /**
   * Save mode-specific statistics to localStorage
   */
  saveModeStats() {
    try {
      localStorage.setItem('scale-degree-mode-stats', JSON.stringify(this.modeStats));
    } catch (error) {
      console.error('Failed to save mode stats to localStorage:', error);
    }
  }

  /**
   * Normalize a key to match our statistics system
   * This handles enharmonic equivalents and ensures we use the right key name
   * @param key - The key to normalize
   * @returns Normalized key that exists in our stats system
   */
  normalizeKey(key: string): string | null {
    // If the key is already in our system, use it directly
    if (this.stats[key]) {
      return key;
    }

    // Check for enharmonic equivalent
    const enharmonic = MusicTheory.ENHARMONIC_MAP[key];
    if (enharmonic && this.stats[enharmonic]) {
      return enharmonic;
    }

    console.error(`Cannot normalize key: ${key}. Available keys:`, Object.keys(this.stats));
    return null;
  }

  /**
   * Record a challenge attempt
   * @param key - The key used
   * @param degree - Scale degree (2-7)
   * @param isCorrect - Whether the answer was correct
   * @param responseTime - Time in milliseconds
   * @param challengeType - The type of challenge (forward or inverse)
   */
  recordAttempt(key: string, degree: number, isCorrect: boolean, responseTime: number, challengeType: ChallengeType = ChallengeType.FORWARD): void {
    // Normalize the key to match our statistics system
    const normalizedKey = this.normalizeKey(key);
    
    if (!normalizedKey) {
      console.error(`Cannot record attempt: invalid key "${key}". Available keys:`, Object.keys(this.stats));
      return;
    }
    
    const keyStats = this.stats[normalizedKey];
    if (!keyStats || !keyStats[degree]) {
      console.error(`Invalid key/degree combination: ${normalizedKey}/${degree}`);
      console.log('Available degrees for key:', Object.keys(keyStats || {}));
      return;
    }

    const degreeStats = keyStats[degree];
    
    // Update basic stats
    degreeStats.attempts++;
    if (isCorrect) {
      degreeStats.correct++;
    }
    
    // Update time stats
    degreeStats.totalTime += responseTime;
    if (!degreeStats.bestTime || responseTime < degreeStats.bestTime) {
      degreeStats.bestTime = responseTime;
    }
    if (!degreeStats.worstTime || responseTime > degreeStats.worstTime) {
      degreeStats.worstTime = responseTime;
    }
    
    // Update recent attempts (keep last 10)
    degreeStats.recentAttempts.push({
      correct: isCorrect,
      time: responseTime,
      timestamp: Date.now(),
      mode: challengeType
    });
    
    if (degreeStats.recentAttempts.length > 10) {
      degreeStats.recentAttempts.shift();
    }
    
    // Also record in mode-specific stats
    this.recordModeAttempt(normalizedKey, degree, isCorrect, responseTime, challengeType);
    
    this.saveStats();
  }

  /**
   * Record a challenge attempt in mode-specific statistics
   * @param key - The normalized key used
   * @param degree - Scale degree (2-7)
   * @param isCorrect - Whether the answer was correct
   * @param responseTime - Time in milliseconds
   * @param challengeType - The type of challenge (forward or inverse)
   */
  private recordModeAttempt(key: string, degree: number, isCorrect: boolean, responseTime: number, challengeType: ChallengeType): void {
    const modeStatsData = challengeType === ChallengeType.FORWARD ? this.modeStats.forward : this.modeStats.inverse;
    const keyStats = modeStatsData[key];
    
    if (!keyStats || !keyStats[degree]) {
      console.error(`Invalid key/degree combination for mode stats: ${key}/${degree}`);
      return;
    }

    const degreeStats = keyStats[degree];
    
    // Update basic stats
    degreeStats.attempts++;
    if (isCorrect) {
      degreeStats.correct++;
    }
    
    // Update time stats
    degreeStats.totalTime += responseTime;
    if (!degreeStats.bestTime || responseTime < degreeStats.bestTime) {
      degreeStats.bestTime = responseTime;
    }
    if (!degreeStats.worstTime || responseTime > degreeStats.worstTime) {
      degreeStats.worstTime = responseTime;
    }
    
    // Update recent attempts (keep last 10)
    degreeStats.recentAttempts.push({
      correct: isCorrect,
      time: responseTime,
      timestamp: Date.now(),
      mode: challengeType
    });
    
    if (degreeStats.recentAttempts.length > 10) {
      degreeStats.recentAttempts.shift();
    }
    
    this.saveModeStats();
  }

  /**
   * Get overall statistics (legacy method for backward compatibility)
   */
  getOverallStats(): OverallStats {
    let totalAttempts = 0;
    let totalCorrect = 0;
    let totalTime = 0;

    for (const key in this.stats) {
      for (const degree in this.stats[key]) {
        const degreeStats = this.stats[key][degree];
        totalAttempts += degreeStats.attempts;
        totalCorrect += degreeStats.correct;
        totalTime += degreeStats.totalTime;
      }
    }

    return {
      totalAttempts,
      totalCorrect,
      accuracy: totalAttempts > 0 ? totalCorrect / totalAttempts : 0,
      averageTime: totalAttempts > 0 ? totalTime / totalAttempts : 0
    };
  }

  /**
   * Get statistics for a specific mode
   * @param mode - The challenge type to get stats for
   */
  getStatsByMode(mode: ChallengeType): OverallStats {
    const modeStatsData = mode === ChallengeType.FORWARD ? this.modeStats.forward : this.modeStats.inverse;
    let totalAttempts = 0;
    let totalCorrect = 0;
    let totalTime = 0;

    for (const key in modeStatsData) {
      for (const degree in modeStatsData[key]) {
        const degreeStats = modeStatsData[key][degree];
        totalAttempts += degreeStats.attempts;
        totalCorrect += degreeStats.correct;
        totalTime += degreeStats.totalTime;
      }
    }

    return {
      totalAttempts,
      totalCorrect,
      accuracy: totalAttempts > 0 ? totalCorrect / totalAttempts : 0,
      averageTime: totalAttempts > 0 ? totalTime / totalAttempts : 0
    };
  }

  /**
   * Get combined statistics across all modes
   */
  getCombinedStats(): OverallStats {
    const forwardStats = this.getStatsByMode(ChallengeType.FORWARD);
    const inverseStats = this.getStatsByMode(ChallengeType.INVERSE);

    const totalAttempts = forwardStats.totalAttempts + inverseStats.totalAttempts;
    const totalCorrect = forwardStats.totalCorrect + inverseStats.totalCorrect;
    const totalTime = (forwardStats.averageTime * forwardStats.totalAttempts) + 
                     (inverseStats.averageTime * inverseStats.totalAttempts);

    return {
      totalAttempts,
      totalCorrect,
      accuracy: totalAttempts > 0 ? totalCorrect / totalAttempts : 0,
      averageTime: totalAttempts > 0 ? totalTime / totalAttempts : 0
    };
  }

  /**
   * Get statistics for a specific key-degree combination
   */
  getKeyDegreeStats(key: string, degree: number): DegreeStats | null {
    const normalizedKey = this.normalizeKey(key);
    if (!normalizedKey) return null;

    return this.stats[normalizedKey]?.[degree] || null;
  }

  /**
   * Get worst performing key-degree combinations (legacy method)
   * @param limit - Number of worst combinations to return
   */
  getWorstCombinations(limit = 3): CombinationStats[] {
    const combinations = [];

    for (const key in this.stats) {
      for (const degree in this.stats[key]) {
        const stats = this.stats[key][degree];
        if (stats.attempts > 0) {
          const lastCorrect = stats.recentAttempts.length > 0 ? stats.recentAttempts[stats.recentAttempts.length - 1].correct : false;
          if (!lastCorrect) {
            combinations.push({
              key,
              degree: parseInt(degree),
              accuracy: stats.correct / stats.attempts,
              averageTime: stats.totalTime / stats.attempts,
              attempts: stats.attempts,
              recentTrend: this.getRecentTrend(stats.recentAttempts)
            });
          }
        }
      }
    }

    // Sort by accuracy (ascending) then by average time (descending)
    combinations.sort((a, b) => {
      if (Math.abs(a.accuracy - b.accuracy) < 0.001) {
        return b.averageTime - a.averageTime;
      }
      return a.accuracy - b.accuracy;
    });

    return combinations.slice(0, limit);
  }

  /**
   * Get worst performing key-degree combinations for a specific mode
   * @param mode - The challenge type to get combinations for
   * @param limit - Number of worst combinations to return
   */
  getWorstCombinationsByMode(mode: ChallengeType, limit = 3): CombinationStats[] {
    const modeStatsData = mode === ChallengeType.FORWARD ? this.modeStats.forward : this.modeStats.inverse;
    const combinations = [];

    for (const key in modeStatsData) {
      for (const degree in modeStatsData[key]) {
        const stats = modeStatsData[key][degree];
        if (stats.attempts > 0) {
          const lastCorrect = stats.recentAttempts.length > 0 ? stats.recentAttempts[stats.recentAttempts.length - 1].correct : false;
          if (!lastCorrect) {
            combinations.push({
              key,
              degree: parseInt(degree),
              accuracy: stats.correct / stats.attempts,
              averageTime: stats.totalTime / stats.attempts,
              attempts: stats.attempts,
              recentTrend: this.getRecentTrend(stats.recentAttempts)
            });
          }
        }
      }
    }

    // Sort by accuracy (ascending) then by average time (descending)
    combinations.sort((a, b) => {
      if (Math.abs(a.accuracy - b.accuracy) < 0.001) {
        return b.averageTime - a.averageTime;
      }
      return a.accuracy - b.accuracy;
    });

    return combinations.slice(0, limit);
  }

  /**
   * Calculate recent performance trend
   */
  getRecentTrend(recentAttempts: Attempt[]): string {
    if (recentAttempts.length < 2) return 'insufficient_data';

    const firstHalf = recentAttempts.slice(0, Math.floor(recentAttempts.length / 2));
    const secondHalf = recentAttempts.slice(Math.floor(recentAttempts.length / 2));

    if (firstHalf.length === 0 || secondHalf.length === 0) return 'insufficient_data';

    const firstHalfAccuracy = firstHalf.filter(a => a.correct).length / firstHalf.length;
    const secondHalfAccuracy = secondHalf.filter(a => a.correct).length / secondHalf.length;

    if (secondHalfAccuracy > firstHalfAccuracy + 0.1) return 'improving';
    if (firstHalfAccuracy > secondHalfAccuracy + 0.1) return 'declining';
    return 'stable';
  }

  /**
   * Get keys that need the most practice
   */
  getWorstKeys(limit = 3): Array<{ key: string; accuracy: number; averageTime: number; attempts: number }> {
    const keyStats: Record<string, { accuracy: number; averageTime: number; attempts: number }> = {};

    for (const key in this.stats) {
      let totalAttempts = 0;
      let totalCorrect = 0;
      let totalTime = 0;

      for (const degree in this.stats[key]) {
        const stats = this.stats[key][degree];
        totalAttempts += stats.attempts;
        totalCorrect += stats.correct;
        totalTime += stats.totalTime;
      }

      if (totalAttempts > 0) {
        keyStats[key] = {
          accuracy: totalCorrect / totalAttempts,
          averageTime: totalTime / totalAttempts,
          attempts: totalAttempts
        };
      }
    }

    return Object.entries(keyStats)
      .sort(([,a], [,b]) => a.accuracy - b.accuracy)
      .slice(0, limit)
      .map(([key, stats]) => ({ key, ...stats }));
  }

  /**
   * Get all statistics data for heatmap visualization (legacy method)
   */
  getAllStats(): Stats {
    return this.stats;
  }

  /**
   * Get statistics data for a specific mode
   * @param mode - The challenge type to get stats for
   */
  getStatsByModeData(mode: ChallengeType): Stats {
    return mode === ChallengeType.FORWARD ? this.modeStats.forward : this.modeStats.inverse;
  }

  /**
   * Reset all statistics
   */
  resetStats() {
    this.stats = this.initializeEmptyStats();
    this.modeStats = {
      forward: this.initializeEmptyStats(),
      inverse: this.initializeEmptyStats()
    };
    this.saveStats();
    this.saveModeStats();
  }

  /**
   * Reset statistics for a specific mode
   * @param mode - The challenge type to reset stats for
   */
  resetStatsByMode(mode: ChallengeType) {
    if (mode === ChallengeType.FORWARD) {
      this.modeStats.forward = this.initializeEmptyStats();
    } else {
      this.modeStats.inverse = this.initializeEmptyStats();
    }
    this.saveModeStats();
  }

  /**
   * Export statistics as JSON
   */
  exportStats() {
    return {
      stats: this.stats,
      overall: this.getOverallStats(),
      exportDate: new Date().toISOString()
    };
  }
}

export default StatisticsManager;